import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../utils/supabase';
import { 
  IoCheckmarkCircleOutline, 
  IoCloseOutline,
  IoArrowForward,
  IoArrowBack,
  IoDocumentTextOutline
} from 'react-icons/io5';
import AchievementSelector from './AchievementSelector';
import TalkingPointsGenerator from './TalkingPointsGenerator';
import ReviewDocumentCreator from './ReviewDocumentCreator';
import { Achievement } from '../../../hooks/useAchievements';

interface ReviewPreparationFlowProps {
  focusId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface PreparationData {
  selectedAchievements: Achievement[];
  keyMetrics: any[];
  talkingPoints: {
    accomplishments: string[];
    challenges: string[];
    growth: string[];
    futureGoals: string[];
  };
  reviewSummary: string;
}

const STEPS = [
  { id: 'achievements', label: 'Select Achievements', icon: '🏆' },
  { id: 'talking-points', label: 'Generate Talking Points', icon: '💬' },
  { id: 'review', label: 'Review & Finalize', icon: '📝' }
];

const ReviewPreparationFlow: React.FC<ReviewPreparationFlowProps> = ({
  focusId,
  isOpen,
  onClose,
  onComplete
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [preparationData, setPreparationData] = useState<PreparationData>({
    selectedAchievements: [],
    keyMetrics: [],
    talkingPoints: {
      accomplishments: [],
      challenges: [],
      growth: [],
      futureGoals: []
    },
    reviewSummary: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Style classes
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAchievementsSelected = (achievements: Achievement[]) => {
    setPreparationData(prev => ({
      ...prev,
      selectedAchievements: achievements
    }));
  };

  const handleTalkingPointsGenerated = (talkingPoints: any, metrics: any[]) => {
    setPreparationData(prev => ({
      ...prev,
      talkingPoints,
      keyMetrics: metrics
    }));
  };

  const handleSaveReview = async (reviewData: any) => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Save to review_preparation table
      const { data: prepData, error: prepError } = await supabase
        .from('review_preparation')
        .insert({
          focus_id: focusId,
          user_id: user.id,
          preparation_type: 'performance_review',
          content: {
            selectedAchievements: preparationData.selectedAchievements,
            keyMetrics: preparationData.keyMetrics,
            talkingPoints: preparationData.talkingPoints,
            ...reviewData
          }
        })
        .select()
        .single();

      if (prepError) throw prepError;

      // Create a review document in the reviews table
      const { data: reviewDoc, error: reviewError } = await supabase
        .from('reviews')
        .insert({
          user_id: user.id,
          review_name: reviewData.title || 'Performance Review Preparation',
          review_text: reviewData.content,
          context_counts: {
            achievements: preparationData.selectedAchievements.length,
            metrics: preparationData.keyMetrics.length,
            talkingPoints: Object.values(preparationData.talkingPoints).flat().length
          },
          career_card_ids: [],
          note_ids: [],
          review_date: new Date().toISOString().split('T')[0],
          review_goal: 'Performance Review Preparation',
          output_style: 'structured',
          is_pinned: false
        })
        .select()
        .single();

      if (reviewError) throw reviewError;

      showToast('Review preparation saved successfully!', 'success');
      onComplete();
      onClose();
      
      // Don't navigate away - the Reviews tab will now show the new review
    } catch (error) {
      console.error('Error saving review preparation:', error);
      showToast('Failed to save review preparation', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50" />

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`relative w-full max-w-5xl max-h-[90vh] ${bgColor} rounded-xl shadow-2xl overflow-hidden`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
            <div>
              <h2 className={`text-2xl font-semibold ${textColor}`}>
                Prepare for Your Performance Review
              </h2>
              <p className={`text-sm ${mutedTextColor} mt-1`}>
                Let's create a comprehensive review document to help you shine
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <IoCloseOutline className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className={`px-6 py-4 border-b ${borderColor}`}>
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center gap-3 ${
                      index === currentStep ? textColor : mutedTextColor
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                        index < currentStep
                          ? 'bg-green-500 text-white'
                          : index === currentStep
                          ? 'bg-purple-600 text-white'
                          : darkMode
                          ? 'bg-gray-700'
                          : 'bg-gray-200'
                      }`}
                    >
                      {index < currentStep ? '✓' : step.icon}
                    </div>
                    <span className={`text-sm font-medium hidden sm:block`}>
                      {step.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`w-12 sm:w-24 h-0.5 mx-2 ${
                        index < currentStep
                          ? 'bg-green-500'
                          : darkMode
                          ? 'bg-gray-700'
                          : 'bg-gray-300'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
            <AnimatePresence mode="wait">
              {currentStep === 0 && (
                <motion.div
                  key="achievements"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <AchievementSelector
                    focusId={focusId}
                    onSelectionChange={handleAchievementsSelected}
                    selectedAchievements={preparationData.selectedAchievements}
                  />
                </motion.div>
              )}

              {currentStep === 1 && (
                <motion.div
                  key="talking-points"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <TalkingPointsGenerator
                    focusId={focusId}
                    selectedAchievements={preparationData.selectedAchievements}
                    onTalkingPointsGenerated={handleTalkingPointsGenerated}
                    existingTalkingPoints={preparationData.talkingPoints}
                  />
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <ReviewDocumentCreator
                    focusId={focusId}
                    preparationData={preparationData}
                    onSave={handleSaveReview}
                    isSaving={isSaving}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className={`flex justify-between items-center p-6 border-t ${borderColor}`}>
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${borderColor} ${
                currentStep === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              } transition-colors`}
            >
              <IoArrowBack className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg border ${borderColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
              >
                Cancel
              </button>
              
              {currentStep < STEPS.length - 1 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 0 && preparationData.selectedAchievements.length === 0)
                  }
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors ${
                    (currentStep === 0 && preparationData.selectedAchievements.length === 0)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  <span>Next</span>
                  <IoArrowForward className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={() => {}} // Will be handled by ReviewDocumentCreator
                  disabled={isSaving}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors ${
                    isSaving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <IoCheckmarkCircleOutline className="w-5 h-5" />
                      <span>Create Review</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReviewPreparationFlow;